import * as React from 'react';
import { FollowerAccess } from '../Registration/Permission/FollowerAccess';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.scss';
import '../../styles/squad-theme/theme/squad-bootstrap.css';
import { IconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faSort,
  faSortUp,
  faSortDown,
  faFilter,
  faQuoteRight,
  faLink,
  faListUl,
  faListOl,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faCode,
  faUnderline,
  faItalic,
  faBold,
  faSyncAlt,
  faEllipsisH,
  faCaretDown,
  faCaretUp,
  faPlusCircle,
  faMinusCircle,
  faClock,
  faPlus,
  faMinus,
  faChevronDown,
  faChevronUp,
  faChevronRight,
  faArrowRight,
  faArrowLeft,
  faFileCsv,
  faVideo,
  faCamera
} from '@fortawesome/free-solid-svg-icons';
import { faFilePdf as farFilePdf } from '@fortawesome/free-regular-svg-icons';
import { faTwitter } from '@fortawesome/free-brands-svg-icons';
import { Redirect, Route, Switch } from 'react-router';
import BPromise from 'bluebird';
import propz from 'propz';
import { Login } from 'Src/views/Login/Login';
import { become, logout, logoutReduced, roleout } from '../../helpers/auth/auth';
import {
  clearRole,
  clearSessions,
  getActivePermission,
  getActiveSchoolId,
  getActiveSchoolName,
  getSessions,
  getUserIdFromSession,
  getUserRoleFromSession
} from '../../helpers/session/session';
import { RoleSession } from '../../models/sessions';
import { UserProfile } from '../../models/profile';
import StorageHelper from '../../helpers/storage/storage';
import { GenericView } from '../GenericView/GenericView';
import { getSchool } from '../../helpers/service/admin/user';
import { getProfile, getRoles } from '../../helpers/service/admin/user';
import { Loader } from '../../components/Loader/Loader';
import 'react-datepicker/dist/react-datepicker.css';
import { School } from '../../models/school';
import { Permission } from '../../models/permission';
import { ResetPassword } from '../ResetPassword/ResetPassword';
import { ProfileRouting } from '../GenericView/Profile/ProfileRouting';
import { Registration } from '../Registration/Registration';
import SignUpLanding from '../Registration/SignupLanding';
import { LoginRoute } from '../LoginRouter/LoginRouter';
import { RoleSelector } from '../LoginRouter/RoleSelector/RoleSelector';
import { ROLE } from '../../consts/user';
import { KIND } from '../../consts/school';
import { isSuperAdminHost } from 'Src/helpers/host/host';
import { SuperAdminApp } from 'Src/views/SuperAdminApp/SuperAdminApp';
import { MobileTurnstile } from 'Src/views/MobileTurnstile/MobileTurnstile';
import '../../styles/filter-panels.scss';
import '../../styles/list-pagination.scss';
import '../../styles/union-list-controls.scss';

// GA4 type declaration
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

function trackPageView(path: string): void {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      send_to: 'G-WJMXSJ1KDP'
    });
  }
}

const AUTH_SYNC_STORAGE_KEY = 'sit.auth.identityChange';

interface AuthSyncPayload {
  userId?: string;
  changedAt: number;
}

function StandalonePage({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="appStandaloneViewport sit-scroll">{children}</div>;
}

library.add(
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faSort,
  faSortUp,
  faSortDown,
  faFilter,
  faQuoteRight,
  faCode,
  faBold,
  faItalic,
  faUnderline,
  faLink,
  faListOl,
  faListUl,
  faAlignLeft,
  faAlignRight,
  faAlignCenter,
  faSyncAlt,
  faEllipsisH,
  faCaretDown,
  faCaretUp,
  faPlusCircle,
  faMinusCircle,
  faClock,
  faPlus,
  faMinus,
  faChevronDown,
  faChevronUp,
  faChevronRight,
  faFileCsv,
  farFilePdf,
  faArrowRight,
  faArrowLeft,
  faTwitter as IconDefinition,
  faVideo,
  faCamera
);

interface State {
  user: AppUser;
  isLoading: boolean;
}

export interface AppUser {
  authorized: boolean;
  userId: string;
  loginSessionKey: string;
  roleSessionKey: string;
  activeRole: string;
  activeSchoolId: string;
  activeSchoolName: string;
  activeSchool: School;
  activePermission: Permission;
  profile: UserProfile;
  isRolesNotExist: boolean;
}

export class App extends React.Component<{}, State> {
  private authChannel?: BroadcastChannel;

  constructor(props) {
    super(props);

    const sessions = getSessions();

    const { loginSession } = sessions;
    let { roleSession } = sessions;

    // A login cookie is shared by browser tabs while role context is kept in
    // sessionStorage. Never bootstrap a page with sessions owned by different
    // users: the role key would otherwise authorize requests as the old user.
    if (loginSession && roleSession && String(loginSession.userId) !== String(roleSession.userId)) {
      clearRole();
      roleSession = undefined;
    }

    const activeSchoolId = roleSession ? getActiveSchoolId() : undefined;
    const activeSchoolName = roleSession ? getActiveSchoolName() : undefined;
    const activePermission = roleSession ? getActivePermission() : undefined;

    const userId = getUserIdFromSession(loginSession, roleSession);
    const role = getUserRoleFromSession(roleSession);

    const isLoginSession = typeof loginSession !== 'undefined';
    const isRoleSession = typeof roleSession !== 'undefined';
    const isRoleExist = typeof role !== 'undefined';

    this.state = {
      user: {
        loginSessionKey: isLoginSession ? loginSession.id || loginSession.key : undefined,
        roleSessionKey: isRoleSession ? roleSession.key : undefined,
        userId: userId,
        authorized: isLoginSession || isRoleSession,
        activeRole: isRoleExist ? role : undefined,
        activeSchoolId: activeSchoolId,
        activeSchoolName: activeSchoolName,
        activeSchool: undefined,
        activePermission: activePermission,
        profile: undefined,
        isRolesNotExist: isRoleExist ? !isRoleExist : undefined
      },
      isLoading: true
    };
  }

  componentDidMount() {
    this.initializeAuthSync();

    const { history } = this.props as any;
    if (history) {
      trackPageView(history.location.pathname);
      history.listen((location: any) => {
        trackPageView(location.pathname);
      });
    }
    const user = this.state.user;
    const { roleSessionKey, activeSchoolId, loginSessionKey } = user;
    const isRoleSessionKeyExist = typeof roleSessionKey !== 'undefined';
    const isLoginSessionKeyExist = typeof loginSessionKey !== 'undefined';

    switch (true) {
      case isRoleSessionKeyExist: {
        this.setState({
          isLoading: true
        });

        const getProfilePromise = getProfile(user);
        const getSchoolPromise = getSchool(user, activeSchoolId);
        const getRolesPromise = getRoles(user);
        const promises = [getProfilePromise, getSchoolPromise, getRolesPromise];

        BPromise.all(promises)
          .then(([profile, school, roles]) => {
            const isRolesNotExist = roles.length === 0;
            this.setState({
              user: {
                ...user,
                activeSchool: school,
                profile,
                isRolesNotExist
              },
              isLoading: false
            });
          })
          .catch(err => {
            console.error('Error during school data fetching in componentDidMount:', err);
            this.setDefaultState();
          });
        break;
      }
      case isLoginSessionKeyExist: {
        const getRolesPromise = getRoles(user);
        const getProfilePromise = getProfile(user);
        const promises = [getProfilePromise, getRolesPromise];

        BPromise.all(promises)
          .then(([profile, roles]) => {
            const isRolesNotExist = roles.length === 0;
            this.setState({
              user: {
                ...user,
                profile,
                isRolesNotExist
              },
              isLoading: false
            });
          })
          .catch(err => {
            this.setDefaultState();
          });
        break;
      }
      default: {
        this.setState({
          isLoading: false
        });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.onAuthStorageEvent);
    if (this.authChannel) {
      this.authChannel.close();
      this.authChannel = undefined;
    }
  }

  initializeAuthSync = (): void => {
    window.addEventListener('storage', this.onAuthStorageEvent);

    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.authChannel = new BroadcastChannel('sit-auth');
        this.authChannel.onmessage = event => this.onExternalAuthChange(event.data);
      } catch (error) {
        this.authChannel = undefined;
      }
    }
  };

  onAuthStorageEvent = (event: StorageEvent): void => {
    if (event.key !== AUTH_SYNC_STORAGE_KEY || !event.newValue) {
      return;
    }

    try {
      this.onExternalAuthChange(JSON.parse(event.newValue));
    } catch (error) {
      // Ignore malformed/non-Squad storage events.
    }
  };

  onExternalAuthChange = (payload: AuthSyncPayload): void => {
    const currentUserId = this.state.user.userId;
    const nextUserId = payload && payload.userId;
    const isSameIdentity =
      typeof currentUserId === 'undefined'
        ? typeof nextUserId === 'undefined'
        : String(currentUserId) === String(nextUserId);

    if (isSameIdentity) {
      return;
    }

    // The login cookie has already changed in the other tab. Drop this tab's
    // old role key before reloading so no further request can use mixed users.
    clearRole();
    window.location.reload();
  };

  broadcastAuthChange = (userId?: string): void => {
    const payload: AuthSyncPayload = { userId, changedAt: Date.now() };

    if (this.authChannel) {
      try {
        this.authChannel.postMessage(payload);
      } catch (error) {
        // localStorage remains as the compatibility fallback.
      }
    }

    try {
      window.localStorage.setItem(AUTH_SYNC_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // Auth still works when browser storage is unavailable.
    }
  };

  onLoginClick = (
    userId: string,
    key: string,
    profile: UserProfile,
    options: { isRolesNotExist?: boolean } = {}
  ): void => {
    const user = this.state.user;
    const isEmailVerified = propz.get(profile, ['verification', 'status', 'email']);
    const isPhoneVerified = propz.get(profile, ['verification', 'status', 'sms']);
    const isUserVerified = isEmailVerified && isPhoneVerified;

    const isSameUser = String(user.userId || '') === String(userId || '');
    const isSessionChanged = user.loginSessionKey !== key || !isSameUser;

    // This callback establishes a login identity. It must never inherit a role,
    // school or permission from the account that happened to be open before it.
    clearRole();
    this.setState({
      user: {
        authorized: true,
        userId: userId,
        loginSessionKey: key,
        roleSessionKey: undefined,
        activeRole: undefined,
        activeSchoolId: undefined,
        activeSchoolName: undefined,
        activeSchool: undefined,
        activePermission: undefined,
        profile,
        isRolesNotExist: !isUserVerified
          ? true
          : typeof options.isRolesNotExist === 'boolean'
          ? options.isRolesNotExist
          : isSameUser
          ? user.isRolesNotExist
          : undefined
      }
    });

    if (isSessionChanged) {
      this.broadcastAuthChange(userId);
    }
  };

  setDefaultState = (): void => {
    clearSessions();

    this.setState({
      user: {
        loginSessionKey: undefined,
        roleSessionKey: undefined,
        userId: undefined,
        authorized: false,
        activeRole: undefined,
        activeSchoolId: undefined,
        activeSchoolName: undefined,
        activeSchool: undefined,
        activePermission: undefined,
        profile: undefined,
        isRolesNotExist: undefined
      }
    });
  };

  roleOut = () => {
    clearRole();

    const { user } = this.state;
    const { roleSessionKey } = user;

    this.setState({
      isLoading: true
    });

    roleout(roleSessionKey).then(() => {
      this.setState({
        isLoading: false,
        user: {
          ...user,
          roleSessionKey: undefined,
          activeRole: undefined,
          activeSchoolId: undefined,
          activeSchoolName: undefined,
          activeSchool: undefined,
          activePermission: undefined
        }
      });
    });
  };

  onLogoutClick = (event): void => {
    event.preventDefault();

    const { loginSessionKey, roleSessionKey } = this.state.user;
    const completeLogout = () => {
      this.setDefaultState();
      this.broadcastAuthChange(undefined);
    };

    if (loginSessionKey && roleSessionKey) {
      logout(loginSessionKey, roleSessionKey).then(completeLogout, completeLogout);
    } else if (roleSessionKey) {
      roleout(roleSessionKey).then(completeLogout, completeLogout);
    } else if (loginSessionKey) {
      logoutReduced(loginSessionKey).then(completeLogout, completeLogout);
    } else {
      completeLogout();
    }
  };

  onRoleChange = (
    roleName: string,
    permissionSchoolId: string,
    permissionSchoolName: string,
    permission: Permission
  ): any => {
    const user = this.state.user;

    StorageHelper.SessionStorage.set('activeSchoolId', permissionSchoolId);
    StorageHelper.SessionStorage.set('activeSchoolName', permissionSchoolName);
    StorageHelper.SessionStorage.set('activePermission', permission);

    const { roleSessionKey } = user;

    this.setState({
      isLoading: true
    });

    return roleout(roleSessionKey)
      .then(() => {
        return become(user, roleName);
      })
      .then((roleSession: RoleSession) => {
        const { role: userRole, key } = roleSession;

        return this.onRoleSelect(userRole, key, permissionSchoolId, permissionSchoolName, roleSession, permission);
      });
  };

  onRoleSelect = (
    roleName: string,
    key: string,
    permissionSchoolId: string,
    permissionSchoolName: string,
    roleSession: RoleSession,
    permission: Permission
  ): void => {
    const user = this.state.user;

    if (!roleSession || String(roleSession.userId) !== String(user.userId)) {
      clearRole();
      this.setState({ isLoading: false });
      return;
    }

    StorageHelper.SessionStorage.set('roleSession', roleSession);
    StorageHelper.SessionStorage.set('activeSchoolId', permissionSchoolId);
    StorageHelper.SessionStorage.set('activeSchoolName', permissionSchoolName);
    StorageHelper.SessionStorage.set('activePermission', permission);

    getSchool(user, permissionSchoolId, key).then(school => {
      this.setState({
        user: {
          ...user,
          roleSessionKey: key,
          activeRole: roleName,
          activeSchoolId: permissionSchoolId,
          activeSchoolName: permissionSchoolName,
          activeSchool: school,
          activePermission: permission,
          isRolesNotExist: false
        },
        isLoading: false
      });
    });
  };

  onSchoolUpdate = () => {
    const user = this.state.user;
    const permissionSchoolId = user.activeSchoolId;
    const key = user.roleSessionKey;

    return getSchool(user, permissionSchoolId, key).then(school => {
      this.setState({
        user: {
          ...user,
          activeSchool: school
        }
      });
    });
  };

  updateProfile = (profile: UserProfile) => {
    const user = this.state.user;
    this.setState({
      user: {
        ...user,
        profile
      }
    });
  };

  updateIsRolesNotExist = (isRolesNotExist: boolean) => {
    const user = this.state.user;
    this.setState({
      user: {
        ...user,
        isRolesNotExist
      }
    });
  };

  render() {
    const { isLoading, user } = this.state;
    const isEmailVerified = propz.get(user, ['profile', 'verification', 'status', 'email']);
    const isPhoneVerified = propz.get(user, ['profile', 'verification', 'status', 'sms']);
    const isUserVerified = isEmailVerified && isPhoneVerified;
    const isUserAuthorized = propz.get(user, ['authorized']);
    const isRolesNotExist = propz.get(user, ['isRolesNotExist']);

    const userActiveRole = propz.get(user, ['activeRole']);
    const isAdmin = userActiveRole === ROLE.ADMIN;
    const userActiveSchoolKind = propz.get(user, ['activeSchool', 'kind']);
    const isSchoolUnion = userActiveSchoolKind === KIND.SCHOOL_UNION;
    const isSchoolUnionAdmin = isAdmin && isSchoolUnion;
    const isSchoolAdmin = isAdmin && !isSchoolUnion;
    const isParent = userActiveRole === ROLE.PARENT;
    const isStudent = userActiveRole === ROLE.STUDENT;

    if (isSuperAdminHost()) {
      return (
        <StandalonePage>
          <SuperAdminApp />
        </StandalonePage>
      );
    }

    if (isLoading) {
      return <Loader />;
    }

    const routeLocation = (this.props as any).location;
    const currentPathname = routeLocation && routeLocation.pathname ? routeLocation.pathname : window.location.pathname;
    const isRegistrationLandingRoute = currentPathname === '/registration' || currentPathname === '/registration/';
    const isRegistrationWizardRoute = currentPathname === '/register' || currentPathname.indexOf('/register/') === 0;

    if (currentPathname === '/follow') {
      const params = new URLSearchParams(routeLocation?.search || window.location.search);
      return <StandalonePage><FollowerAccess key={params.get('union') || 'choose'} user={user} unionId={params.get('union') || ''} /></StandalonePage>;
    }

    if (isRegistrationLandingRoute) {
      return (
        <StandalonePage>
          <SignUpLanding />
        </StandalonePage>
      );
    }

    // Keep the same Registration component mounted while a newly-created,
    // unverified user replaces an account that was already open in this tab.
    if (isRegistrationWizardRoute) {
      return (
        <Switch>
          <Route
            path="/register"
            render={props => (
              <StandalonePage>
                <Registration {...props} user={user} onFormSubmit={this.onLoginClick} />
              </StandalonePage>
            )}
          />
        </Switch>
      );
    }

    if (isUserAuthorized && !isUserVerified) {
      return (
        <StandalonePage>
          <Switch>
            <Route
              path="/profile"
              component={props => (
                <ProfileRouting
                  user={user}
                  updateProfile={this.updateProfile}
                  onLogoutClick={this.onLogoutClick}
                  {...props}
                />
              )}
            />
            <Route
              path="/"
              render={props => <Redirect to={{ pathname: '/profile/verification', state: { from: props.location } }} />}
            />
          </Switch>
        </StandalonePage>
      );
    }

    if (isUserAuthorized && isRolesNotExist) {
      return (
        <StandalonePage>
          <Switch>
            <Route
              path="/profile"
              component={props => (
                <ProfileRouting
                  user={user}
                  updateProfile={this.updateProfile}
                  onLogoutClick={this.onLogoutClick}
                  {...props}
                />
              )}
            />
            <Route
              path="/"
              render={props => <Redirect to={{ pathname: '/profile/requests', state: { from: props.location } }} />}
            />
          </Switch>
        </StandalonePage>
      );
    }

    return (
      <Switch>
        <Route
          exact
          path="/mobile-turnstile"
          render={() => (
            <StandalonePage>
              <MobileTurnstile />
            </StandalonePage>
          )}
        />
        <Route
          exact
          path="/login"
          component={props => (
            <StandalonePage>
              <Login onFormSubmit={this.onLoginClick} {...props} user={user} />
            </StandalonePage>
          )}
        />
        <Route
          path="/resetPassword"
          component={props => (
            <StandalonePage>
              <ResetPassword {...props} />
            </StandalonePage>
          )}
        />
        <LoginRoute
          exact
          path="/role"
          component={props => (
            <StandalonePage>
              <RoleSelector
                user={user}
                onRoleSelect={this.onRoleSelect}
                setDefaultState={this.setDefaultState}
                updateIsRolesNotExist={this.updateIsRolesNotExist}
                {...props}
              />
            </StandalonePage>
          )}
          user={user}
        />

        <Route
          exact
          path="/"
          render={props => {
            if (!user.activeRole) {
              return (
                <Redirect
                  to={{
                    pathname: '/role',
                    state: { from: { pathname: props.location.pathname, search: props.location.search } }
                  }}
                />
              );
            }

            const defaultPath = userActiveRole === ROLE.SLT
              ? '/slt/overview'
              : isSchoolUnionAdmin
              ? '/dashboard'
              : isSchoolAdmin
              ? '/dashboard'
              : isParent || isStudent
              ? '/home'
              : '/calendar';

            return (
              <Redirect
                to={{
                  pathname: defaultPath,
                  state: { from: { pathname: props.location.pathname, search: props.location.search } }
                }}
              />
            );
          }}
        />

        <Route
          path="/"
          render={props =>
            user.activeRole ? (
              <GenericView
                onRoleChange={this.onRoleChange}
                onLogoutClick={this.onLogoutClick}
                user={user}
                onSchoolUpdate={this.onSchoolUpdate}
                updateProfile={this.updateProfile}
                setAppDefaultState={this.setDefaultState}
                roleOut={this.roleOut}
                {...props}
              />
            ) : (
              <Redirect
                to={{
                  pathname: '/role',
                  state: {
                    from: {
                      pathname: props.location.pathname,
                      search: props.location.search
                    }
                  }
                }}
              />
            )
          }
        />
      </Switch>
    );
  }
}
